import React, { FC, useEffect, useRef, useState } from 'react';
import { IconButton, IconButtonProps, Tooltip } from '@wix/design-system';
import { Heart, HeartFilled } from '@wix/wix-ui-icons-common';
import useMobileMediaQuery from '@components/Hooks/useMobileMediaQuery';
import s from './FavoriteButton.module.scss';

interface FavoriteButtonProps {
  listingId?: string | null;
  isFavorite: boolean;
  isLoading?: boolean;
  addLabel: string;
  removeLabel: string;
  onToggle: (listingId: string, shouldFavorite: boolean) => void;
  skin?: 'light' | 'standard' | 'dark';
  size?: IconButtonProps['size'];
}

const FavoriteButton: FC<FavoriteButtonProps> = ({
  listingId,
  isFavorite,
  isLoading,
  addLabel,
  removeLabel,
  onToggle,
  skin = 'light',
  size = 'medium',
}) => {
  const isMobile = useMobileMediaQuery(false);
  const [isHovered, setIsHovered] = useState(false);
  const isHoveredRef = useRef(false);
  const [clickedState, setClickedState] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isHoveredRef.current) {
      setClickedState(null);
    }
  }, [isFavorite]);
  const effectiveFavorite =
    clickedState !== null && !isMobile ? clickedState : isFavorite;
  const label = effectiveFavorite ? removeLabel : addLabel;
  const getDesktopDisplayFilledHeart = () => {
    if (clickedState !== null) return clickedState;
    return isHovered ? !isFavorite : isFavorite;
  };
  const displayFilledHeart = isMobile
    ? isFavorite
    : getDesktopDisplayFilledHeart();
  const Icon = displayFilledHeart ? HeartFilled : Heart;

  const stopCardNavigation = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const stopCardNavigationPropagationOnly = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const isStandard = skin === 'standard' || skin === 'dark';
  const isDisabled = !listingId || isLoading;

  const button = (
    <IconButton
      ariaLabel={label}
      aria-label={label}
      className={`${isStandard ? s.standardButton : s.favoriteButton} ${
        isDisabled ? s.buttonDisabled : ''
      }`}
      dataHook={`template-favorite-button-${listingId}`}
      disabled={!listingId || isLoading}
      onClick={(event) => {
        stopCardNavigation(event);
        if (listingId) {
          setClickedState(!isFavorite);
          onToggle(listingId, !isFavorite);
        }
      }}
      onMouseDown={stopCardNavigationPropagationOnly}
      onMouseEnter={() => {
        if (!isDisabled) {
          isHoveredRef.current = true;
          setIsHovered(true);
        }
      }}
      onMouseLeave={() => {
        if (!isDisabled) {
          isHoveredRef.current = false;
          setIsHovered(false);
          setClickedState(null);
        }
      }}
      onPointerDown={stopCardNavigationPropagationOnly}
      priority="secondary"
      size={size}
      skin={skin}
    >
      <span
        className={
          isStandard
            ? s.standardIcon
            : `${s.favoriteIcon} ${displayFilledHeart ? s.filledIcon : ''}`
        }
        data-hook={
          displayFilledHeart
            ? 'favorite-button-icon-filled'
            : 'favorite-button-icon-empty'
        }
      >
        <Icon />
      </span>
    </IconButton>
  );

  if (isMobile || clickedState !== null || isDisabled) {
    return button;
  }

  return (
    <Tooltip
      content={label}
      placement="top"
      size="small"
      moveBy={{ x: 0, y: 6 }}
    >
      {button}
    </Tooltip>
  );
};

export default FavoriteButton;
